import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSpinner, faTrash, faWandMagicSparkles } from '@fortawesome/pro-solid-svg-icons';
import { useApplicationContext } from 'ApplicationContext';
import { settingsApi } from 'Pages/Admin/Settings/settingsApi';
import { CatalogStructure, ConfigurationTemplateDto } from './catalogStructure';
import { RequirementView } from './calculatorApi';

interface PositionRow {
    requirementId: string;
    quantity: number;
    hint: string;
}

interface OptionalRow {
    requirementId: string;
    quantity: number;
    question: string;
    preselected: boolean;
}

interface OpenRow {
    groupPath: string;
    quantity: number;
    question: string;
}

/**
 * Vorlagen bearbeiten.
 *
 * **Warum das Formular dreigeteilt ist.** Eine Vorlage besteht aus drei verschiedenen Dingen, und
 * sie zu vermischen wäre der Fehler. „Steht fest" ist eine Position. „Üblich, aber zu bestätigen"
 * ist eine Ja/Nein-Frage — die Anforderung ist bekannt, nur nicht, ob sie gewollt ist. „Menge
 * bekannt, Auswahl offen" ist eine Welche-Frage. Der Editor bildet alle drei getrennt ab, damit
 * beim Pflegen derselbe Unterschied sichtbar bleibt, den der Assistent im Gespräch macht.
 *
 * **Warum kein Freitext für Ids.** Anforderungen und Bereiche werden ausgewählt, nicht getippt.
 * Ein Tippfehler in einer Anforderungs-Id fällt sonst erst auf, wenn die Vorlage im Kundengespräch
 * ins Leere greift.
 */
export const TemplateEditDialog: React.FC<{
    open: boolean;
    /** Bestehende Vorlage, oder null für eine neue. */
    template: ConfigurationTemplateDto | null;
    structure: CatalogStructure;
    requirements: RequirementView[];
    onClose: () => void;
    onSaved: () => void;
}> = ({ open, template, structure, requirements, onClose, onSaved }) => {
    const context = useApplicationContext();
    const http = useMemo(() => settingsApi(context.api), [context.api]);

    const [id, setId] = useState('');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [anlagenart, setAnlagenart] = useState('');
    const [kundentyp, setKundentyp] = useState('');
    const [stichworte, setStichworte] = useState('');
    const [isDraft, setIsDraft] = useState(true);
    const [positions, setPositions] = useState<PositionRow[]>([]);
    const [optionalRows, setOptionalRows] = useState<OptionalRow[]>([]);
    const [openRows, setOpenRows] = useState<OpenRow[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /**
     * Die Beschreibung, aus der eine Vorlage entsteht.
     *
     * Bewusst kein Dialog: der Bediener schreibt einmal auf, was in das Fahrzeug gehört, bekommt
     * einen Entwurf ins Formular und arbeitet dort weiter. Rückfragen gehören ans Telefon, nicht
     * an den Schreibtisch — deshalb ein Textfeld und ein Knopf, kein Gesprächsverlauf.
     */
    const [describe, setDescribe] = useState('');
    const [generating, setGenerating] = useState(false);
    const [notes, setNotes] = useState<string[]>([]);

    useEffect(() => {
        if (!open) return;
        setError(null);
        setId(template?.id ?? '');
        setTitle(template?.title ?? '');
        setDescription(template?.description ?? '');
        setAnlagenart(template?.anlagenart ?? '');
        setKundentyp((template?.kundentyp ?? []).join(' '));
        setStichworte((template?.stichworte ?? []).join(', '));
        setIsDraft(template?.isDraft ?? true);
        setPositions((template?.positions ?? []).map((p) => ({
            requirementId: p.requirementId, quantity: p.quantity, hint: p.hint ?? '',
        })));
        setOptionalRows((template?.optionalPositions ?? []).map((o) => ({
            requirementId: o.requirementId, quantity: o.quantity, question: o.question,
            preselected: o.preselected,
        })));
        setOpenRows((template?.openPositions ?? []).map((o) => ({
            groupPath: o.groupPath, quantity: o.quantity, question: o.question,
        })));
    }, [open, template]);

    const groupPaths = useMemo(
        () => structure.groups.map((g) => g.path).sort(),
        [structure],
    );

    const canSave = id.trim().length > 0 && title.trim().length > 0
        && positions.every((p) => p.requirementId)
        && optionalRows.every((o) => o.requirementId && o.question.trim())
        && openRows.every((o) => o.groupPath && o.question.trim());

    /** Steht schon etwas im Formular? Dann ist die Eingabe eine Änderung, keine Beschreibung. */
    const hasContent = title.trim().length > 0
        || positions.length > 0 || optionalRows.length > 0 || openRows.length > 0;

    const generate = async () => {
        if (!describe.trim()) return;
        setGenerating(true);
        setError(null);
        setNotes([]);
        try {
            const r = await http.post<{
                success: boolean;
                error: string | null;
                notes: string[];
                template: {
                    title: string; description: string | null; anlagenart: string | null;
                    kundentyp: string[]; stichworte: string[]; isDraft: boolean;
                    positions: { requirementId: string; quantity: number; hint: string | null }[];
                    optionalPositions: { requirementId: string; quantity: number; question: string; preselected: boolean }[];
                    openPositions: { groupPath: string; quantity: number; question: string }[];
                } | null;
            }>('/TemplateGenerator/suggest', undefined, {
                description: describe,
                // Der Stand aus dem FORMULAR, nicht der gespeicherte: sonst verlöre jede zweite
                // Änderung die erste, und „und jetzt noch das GSM raus" arbeitete gegen einen
                // Stand, den niemand mehr auf dem Schirm hat.
                current: hasContent ? {
                    title, description, anlagenart: anlagenart || null,
                    kundentyp: kundentyp.split(/\s+/).filter(Boolean),
                    stichworte: stichworte.split(',').map((w) => w.trim()).filter(Boolean),
                    isDraft,
                    positions: positions.map((p) => ({
                        requirementId: p.requirementId, quantity: p.quantity, hint: p.hint || null,
                    })),
                    optionalPositions: optionalRows,
                    openPositions: openRows,
                } : null,
            });

            if (!r.data.success || !r.data.template) {
                setError(r.data.error ?? 'Es kam kein verwertbarer Vorschlag zurück.');
                return;
            }

            const t = r.data.template;
            setTitle(t.title);
            setDescription(t.description ?? '');
            setAnlagenart(t.anlagenart ?? '');
            setKundentyp((t.kundentyp ?? []).join(' '));
            setStichworte((t.stichworte ?? []).join(', '));
            setIsDraft(true);
            setPositions((t.positions ?? []).map((p) => ({
                requirementId: p.requirementId, quantity: p.quantity, hint: p.hint ?? '',
            })));
            setOptionalRows((t.optionalPositions ?? []).map((o) => ({
                requirementId: o.requirementId, quantity: o.quantity,
                question: o.question, preselected: o.preselected,
            })));
            setOpenRows((t.openPositions ?? []).map((o) => ({
                groupPath: o.groupPath, quantity: o.quantity, question: o.question,
            })));
            setNotes(r.data.notes ?? []);
            // Die Anweisung ist eingearbeitet. Bliebe sie stehen, wirkte der nächste Klick sie
            // ein zweites Mal auf den bereits geänderten Stand an.
            setDescribe('');
        } catch {
            setError('Der Vorschlag konnte nicht erzeugt werden.');
        } finally {
            setGenerating(false);
        }
    };

    const save = async () => {
        setBusy(true);
        setError(null);
        try {
            await http.put(`/Calculator/edit/template/${encodeURIComponent(id.trim())}`, {
                title: title.trim(),
                description: description.trim() || null,
                anlagenart: anlagenart || null,
                kundentyp: kundentyp.split(/\s+/).filter(Boolean),
                stichworte: stichworte.split(',').map((w) => w.trim()).filter(Boolean),
                isDraft,
                positions: positions.map((p) => ({
                    requirementId: p.requirementId,
                    quantity: p.quantity,
                    hint: p.hint.trim() || null,
                })),
                optionalPositions: optionalRows.map((o) => ({
                    requirementId: o.requirementId,
                    quantity: o.quantity,
                    question: o.question.trim(),
                    preselected: o.preselected,
                })),
                openPositions: openRows.map((o) => ({
                    groupPath: o.groupPath,
                    quantity: o.quantity,
                    question: o.question.trim(),
                })),
            });
            onSaved();
            onClose();
        } catch {
            setError('Speichern fehlgeschlagen. Fehlt die Berechtigung für die Stammdaten?');
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        if (!template) return;
        if (!window.confirm(`Vorlage ${template.id} löschen?`)) return;
        setBusy(true);
        try {
            await http.delete(`/Calculator/edit/template/${encodeURIComponent(template.id)}`);
            onSaved();
            onClose();
        } catch {
            setError('Löschen fehlgeschlagen.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle>{template ? `Vorlage ${template.id}` : 'Neue Vorlage'}</DialogTitle>

            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Stack spacing={2} sx={{ mt: 1 }}>
                    {/*
                      * Der Einstieg über Sprache statt über Formularfelder. Wer eine Vorlage anlegt,
                      * hat sie im Kopf als Satz — nicht als Liste von Anforderungs-Ids.
                      */}
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            {hasContent ? 'Vorlage ändern' : 'Vorlage beschreiben'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {hasContent
                                ? 'Sag, was sich ändern soll — „GSM raus, dafür zwei DECT-Telefone rein", '
                                  + '„Funk auf 6 erhöhen", „EMMA soll optional sein". Alles Nichtgenannte '
                                  + 'bleibt, wie es unten steht.'
                                : 'Schreib auf, was in das Fahrzeug gehört — in deinen Worten. Der '
                                  + 'Vorschlag landet unten im Formular und lässt sich dort weiter bearbeiten.'}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            size="small"
                            sx={{ mt: 1 }}
                            placeholder={hasContent
                                ? 'z. B. Das GSM-Gateway raus, dafür eine DECT-Anlage mit vier '
                                  + 'Telefonen.\nFunk auf 6 Anschaltungen erhöhen.\n'
                                  + 'Die Navigation soll nicht mehr vorbelegt sein.'
                                : 'z. B. Leichter Befehlskraftwagen der Polizei.\n'
                                  + '4 Tetra-Funkgeräte, meist Motorola.\n'
                                  + '2 Arbeitsplätze mit UniKey TS2, Headset und Fußtaster.\n'
                                  + '2-fach GSM-Gateway mit 2 SIP-Leitungen. EMMA optional.'}
                            value={describe}
                            onChange={(e) => setDescribe(e.target.value)}
                        />
                        <Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: 'center' }}>
                            <Button
                                size="small"
                                variant="contained"
                                disabled={generating || !describe.trim()}
                                startIcon={<FontAwesomeIcon icon={generating ? faSpinner : faWandMagicSparkles} spin={generating} />}
                                onClick={generate}
                            >
                                {generating
                                    ? 'Wird erzeugt …'
                                    : hasContent ? 'Änderung einarbeiten' : 'Vorschlag erzeugen'}
                            </Button>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Nichts wird gespeichert — erst „Speichern" schreibt in den Katalog.
                            </Typography>
                        </Stack>
                    </Paper>

                    {notes.length > 0 && (
                        <Alert severity="warning">
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Nicht zugeordnet
                            </Typography>
                            {notes.map((n) => (
                                <Typography key={n} variant="caption" sx={{ display: 'block' }}>
                                    · {n}
                                </Typography>
                            ))}
                        </Alert>
                    )}

                    <Stack direction="row" spacing={2}>
                        <TextField
                            size="small"
                            label="Kennung"
                            value={id}
                            disabled={!!template}
                            onChange={(e) => setId(e.target.value)}
                            placeholder="V.ELW1"
                            helperText={template ? 'Nicht änderbar' : 'Eindeutig, z. B. V.ELW1'}
                            sx={{ width: 220 }}
                        />
                        <TextField
                            size="small"
                            label="Name"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="ELW 1 — Einsatzleitwagen 1"
                            fullWidth
                        />
                    </Stack>

                    <TextField
                        size="small"
                        label="Beschreibung"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        multiline
                        minRows={2}
                        fullWidth
                    />

                    <Stack direction="row" spacing={2}>
                        {/*
                          * Auswahl mit Freitext: die Liste kommt aus den Stammdaten, aber wer eine
                          * neue Art braucht, soll sie hier eintragen können statt auf ein Release
                          * zu warten. Sie landet dann als Wert an der Vorlage — in die Liste
                          * aufgenommen wird sie im Katalog.
                          */}
                        <Autocomplete
                            freeSolo
                            size="small"
                            sx={{ width: 220 }}
                            options={(structure.anlagenarten ?? []).map((a) => a.name)}
                            value={anlagenart}
                            onChange={(_, v) => setAnlagenart(v ?? '')}
                            onInputChange={(_, v) => setAnlagenart(v)}
                            renderOption={(props, name) => {
                                const art = (structure.anlagenarten ?? []).find((a) => a.name === name);
                                return (
                                    <li {...props} key={name}>
                                        <Stack>
                                            <Typography variant="body2">{name}</Typography>
                                            {art?.description && (
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {art.description}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </li>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Anlagenart"
                                    helperText="Blendet unpassende Themen aus"
                                />
                            )}
                        />
                        <TextField
                            size="small"
                            label="Kundentyp"
                            value={kundentyp}
                            onChange={(e) => setKundentyp(e.target.value)}
                            placeholder="Feuerwehr THW"
                            helperText="Leerzeichengetrennt"
                            sx={{ width: 240 }}
                        />
                        <FormControlLabel
                            control={<Switch checked={isDraft} onChange={(e) => setIsDraft(e.target.checked)} />}
                            label="Entwurf"
                        />
                    </Stack>

                    <TextField
                        size="small"
                        label="Stichworte"
                        value={stichworte}
                        onChange={(e) => setStichworte(e.target.value)}
                        placeholder="ELW1, ELW 1, Einsatzleitwagen"
                        helperText="Kommagetrennt — daran erkennt der Assistent die Vorlage im Gespräch"
                        fullWidth
                    />

                    <Divider />

                    <Box>
                        <Typography variant="subtitle2">Steht fest</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Positionen, die beim Übernehmen sofort gesetzt werden.
                        </Typography>

                        <Stack spacing={1} sx={{ mt: 1 }}>
                            {positions.map((p, i) => (
                                <Paper key={`pos-${i}`} variant="outlined" sx={{ p: 1 }}>
                                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                        <Autocomplete
                                            size="small"
                                            sx={{ flexGrow: 1 }}
                                            options={requirements}
                                            getOptionLabel={(r) => `${r.id} — ${r.titel || ''}`}
                                            isOptionEqualToValue={(a, b) => a.id === b.id}
                                            value={requirements.find((r) => r.id === p.requirementId) ?? null}
                                            onChange={(_, r) => {
                                                const next = [...positions];
                                                next[i] = { ...p, requirementId: r?.id ?? '' };
                                                setPositions(next);
                                            }}
                                            renderInput={(params) => <TextField {...params} label="Anforderung" />}
                                        />
                                        <TextField
                                            size="small"
                                            type="number"
                                            label="Menge"
                                            value={p.quantity}
                                            onChange={(e) => {
                                                const next = [...positions];
                                                next[i] = { ...p, quantity: Number(e.target.value) || 0 };
                                                setPositions(next);
                                            }}
                                            sx={{ width: 100 }}
                                        />
                                        <TextField
                                            size="small"
                                            label="Hinweis"
                                            value={p.hint}
                                            onChange={(e) => {
                                                const next = [...positions];
                                                next[i] = { ...p, hint: e.target.value };
                                                setPositions(next);
                                            }}
                                            sx={{ width: 240 }}
                                        />
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => setPositions(positions.filter((_, j) => j !== i))}
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </IconButton>
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>

                        <Button
                            size="small"
                            startIcon={<FontAwesomeIcon icon={faPlus} />}
                            onClick={() => setPositions([...positions, { requirementId: '', quantity: 1, hint: '' }])}
                            sx={{ mt: 1 }}
                        >
                            Position
                        </Button>
                    </Box>

                    <Divider />

                    <Box>
                        <Typography variant="subtitle2">Üblich, aber zu bestätigen</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Die Anforderung steht fest, die Frage lautet OB. Ein GSM-Gateway gehört
                            in viele Einsatzleitwagen und in manche eben nicht. „Vorbelegt" heißt:
                            kommt beim Übernehmen mit und wird trotzdem bestätigt.
                        </Typography>

                        <Stack spacing={1} sx={{ mt: 1 }}>
                            {optionalRows.map((o, i) => (
                                <Paper key={`opt-${i}`} variant="outlined" sx={{ p: 1 }}>
                                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                        <Autocomplete
                                            size="small"
                                            sx={{ width: 280 }}
                                            options={requirements}
                                            getOptionLabel={(r) => `${r.id} — ${r.titel || ''}`}
                                            isOptionEqualToValue={(a, b) => a.id === b.id}
                                            value={requirements.find((r) => r.id === o.requirementId) ?? null}
                                            onChange={(_, r) => {
                                                const next = [...optionalRows];
                                                next[i] = { ...o, requirementId: r?.id ?? '' };
                                                setOptionalRows(next);
                                            }}
                                            renderInput={(params) => <TextField {...params} label="Anforderung" />}
                                        />
                                        <TextField
                                            size="small"
                                            type="number"
                                            label="Menge"
                                            value={o.quantity}
                                            onChange={(e) => {
                                                const next = [...optionalRows];
                                                next[i] = { ...o, quantity: Number(e.target.value) || 0 };
                                                setOptionalRows(next);
                                            }}
                                            sx={{ width: 90 }}
                                        />
                                        <TextField
                                            size="small"
                                            label="Ja/Nein-Frage"
                                            value={o.question}
                                            onChange={(e) => {
                                                const next = [...optionalRows];
                                                next[i] = { ...o, question: e.target.value };
                                                setOptionalRows(next);
                                            }}
                                            fullWidth
                                        />
                                        <FormControlLabel
                                            control={(
                                                <Switch
                                                    size="small"
                                                    checked={o.preselected}
                                                    onChange={(e) => {
                                                        const next = [...optionalRows];
                                                        next[i] = { ...o, preselected: e.target.checked };
                                                        setOptionalRows(next);
                                                    }}
                                                />
                                            )}
                                            label={<Typography variant="caption">vorbelegt</Typography>}
                                            sx={{ flexShrink: 0, mr: 0 }}
                                        />
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => setOptionalRows(optionalRows.filter((_, j) => j !== i))}
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </IconButton>
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>

                        <Button
                            size="small"
                            startIcon={<FontAwesomeIcon icon={faPlus} />}
                            onClick={() => setOptionalRows([...optionalRows,
                                { requirementId: '', quantity: 1, question: '', preselected: false }])}
                            sx={{ mt: 1 }}
                        >
                            Optionaler Punkt
                        </Button>
                    </Box>

                    <Divider />

                    <Box>
                        <Typography variant="subtitle2">Menge bekannt, Auswahl offen</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Wird NICHT gesetzt. Der Assistent stellt die Frage und ergänzt danach —
                            dass ein ELW 1 drei Funkanschaltungen hat, ist Erfahrung; welches Gerät
                            daran hängt, weiß nur der Kunde.
                        </Typography>

                        <Stack spacing={1} sx={{ mt: 1 }}>
                            {openRows.map((o, i) => (
                                <Paper key={`open-${i}`} variant="outlined" sx={{ p: 1 }}>
                                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                        <Autocomplete
                                            size="small"
                                            sx={{ width: 240 }}
                                            options={groupPaths}
                                            value={o.groupPath || null}
                                            onChange={(_, v) => {
                                                const next = [...openRows];
                                                next[i] = { ...o, groupPath: v ?? '' };
                                                setOpenRows(next);
                                            }}
                                            renderInput={(params) => <TextField {...params} label="Bereich" />}
                                        />
                                        <TextField
                                            size="small"
                                            type="number"
                                            label="Menge"
                                            value={o.quantity}
                                            onChange={(e) => {
                                                const next = [...openRows];
                                                next[i] = { ...o, quantity: Number(e.target.value) || 0 };
                                                setOpenRows(next);
                                            }}
                                            sx={{ width: 100 }}
                                        />
                                        <TextField
                                            size="small"
                                            label="Rückfrage"
                                            value={o.question}
                                            onChange={(e) => {
                                                const next = [...openRows];
                                                next[i] = { ...o, question: e.target.value };
                                                setOpenRows(next);
                                            }}
                                            fullWidth
                                        />
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => setOpenRows(openRows.filter((_, j) => j !== i))}
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </IconButton>
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>

                        <Button
                            size="small"
                            startIcon={<FontAwesomeIcon icon={faPlus} />}
                            onClick={() => setOpenRows([...openRows, { groupPath: '', quantity: 1, question: '' }])}
                            sx={{ mt: 1 }}
                        >
                            Offener Punkt
                        </Button>
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions>
                {template && (
                    <Button color="error" onClick={remove} disabled={busy} sx={{ mr: 'auto' }}>
                        Löschen
                    </Button>
                )}
                <Button onClick={onClose} disabled={busy}>Abbrechen</Button>
                <Button variant="contained" onClick={save} disabled={busy || !canSave}>
                    Speichern
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TemplateEditDialog;
